/**
 * Preferences and account controls.
 *
 * Everything on this panel is stored against the account, not the device: the
 * store queues each change to PUT /api/portfolio/settings, so signing in on a
 * phone brings the same language, currency and payoff strategy with it.
 *
 * Laid out as the "Preferencje v2" design: a window with its sections down the
 * left (General, Currencies & rates, Finance, Notifications, AI & privacy,
 * Account & data) and the chosen one on the right. On a phone it fills the
 * screen and goes one level at a time -- the list of sections, each with a
 * line saying what is set, then the section itself behind a back arrow.
 * Nothing has a SAVE button any more: a choice saves when it is made, a typed
 * figure when the field is left, and the header says so.
 */

import { h } from '../vdom.js';
import {
  C, MONO, SANS, css, input, select, tap, button, saveFile, legalLinks,
  journalUsed, createInputs
} from './common.js';
import { LANGUAGES } from '../i18n.js';
import { APP_VERSION } from '../version.js';
import { localDay, numberText,
  TABS, DEBT_STRATEGIES, UNIT_SYSTEMS, SUGGESTED_CURRENCIES, currentMonth
} from '../../../shared/model.js';
import { PRICES, PRICE_OPTIONS, formatPrice } from '../../../shared/plans.js';

/** The sections, in the order the design lists them. `plan` is reached from the card above them. */
export const SETTINGS_SECTIONS = ['general', 'currencies', 'finance', 'reminders', 'ai', 'account'];
const AUTOSAVES = new Set(['general', 'currencies', 'finance', 'reminders']);
const ICONS = { general: '◐', currencies: '¤', finance: '≡', reminders: '◔', ai: '✦', account: '◉', plan: '+' };

export function renderSettings(ctx) {
  const { state, t, actions, mobile } = ctx;
  const s = t.shell;
  const chosen = state.settingsSection;
  /* A phone opens on the list; a wider screen always has a section beside it. */
  const current = chosen || (mobile ? null : 'general');
  const showNav = !mobile || !current;
  const showBody = Boolean(current);

  return h('div', {
    key: 'settings',
    /*
     * A plain click target, deliberately not role="button": the backdrop wraps
     * the whole panel, so announcing it as a button would read every control
     * inside it as that button's label. Keyboard users close with Escape (see
     * the handler in main.js) or the × in the corner.
     */
    onclick: event => {
      if (event.target === event.currentTarget) actions.closeSettings();
    },
    style: css({
      position: 'fixed', inset: 0, zIndex: 60, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: mobile ? 0 : '24px',
      background: 'rgba(4,8,12,.72)', backdropFilter: 'blur(3px)'
    })
  },
    /* Named as a dialog, so a screen reader says where focus went rather than reading on into the page. */
    h('div', {
      role: 'dialog', 'aria-modal': 'true', 'aria-label': s.settingsTitle,
      style: css({
        width: mobile ? '100%' : 'min(1040px, calc(100vw - 48px))',
        height: mobile ? '100%' : 'min(780px, calc(100vh - 48px))',
        display: 'flex', overflow: 'hidden', fontFamily: SANS, color: C.text,
        background: C.card, borderRadius: mobile ? 0 : '18px',
        border: mobile ? 'none' : `1px solid ${C.strokeHi}`,
        boxShadow: '0 30px 80px rgba(0,0,0,.55)',
        paddingTop: mobile ? 'env(safe-area-inset-top, 0px)' : null
      })
    },
      showNav && sectionNav(ctx, current),
      showBody && sectionBody(ctx, current)
    )
  );
}

// ------------------------------------------------------------ left column ---

function sectionNav(ctx, current) {
  const { state, t, actions, mobile } = ctx;
  const s = t.shell;

  return h('div', {
    key: 'nav',
    style: css({
      width: mobile ? '100%' : '260px', flex: mobile ? '1 1 auto' : '0 0 260px',
      display: 'flex', flexDirection: 'column', minHeight: 0, background: C.panel,
      borderRight: mobile ? 'none' : `1px solid ${C.line}`
    })
  },
    h('div', {
      style: css({ display: 'flex', alignItems: 'center', gap: '10px', padding: mobile ? '14px 16px 12px' : '20px 20px 14px' })
    },
      h('span', { style: css({ flex: 1, fontSize: mobile ? '20px' : '18px', fontWeight: 700, letterSpacing: '-0.3px' }) }, s.settingsTitle),
      mobile && closeButton(ctx, '44px')
    ),
    planCard(ctx, current),
    h('div', {
      style: css({ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 8px 12px' })
    },
      ...SETTINGS_SECTIONS.map(id => {
        const on = !mobile && current === id;
        const [label] = s.prefSections[id];
        return h('div', {
          key: id,
          ...tap(() => actions.setSettingsSection(id), label),
          'aria-current': on ? 'page' : null,
          class: 'pt-nav',
          style: css({
            minHeight: mobile ? '60px' : '48px', display: 'flex', alignItems: 'center', gap: '12px',
            padding: '0 12px', borderRadius: '10px', cursor: 'pointer',
            background: on ? C.line : 'transparent', boxShadow: on ? `inset 2px 0 0 ${C.accent}` : 'none'
          })
        },
          h('span', { style: css({ width: '22px', textAlign: 'center', fontSize: '14px', color: on ? C.accent : C.muted }) }, ICONS[id]),
          h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }) },
            h('span', { style: css({ fontSize: '13.5px', fontWeight: on ? 700 : 500, color: on ? C.text : C.textDim }) }, label),
            mobile && h('span', {
              key: 'summary',
              style: css({ fontSize: '12px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })
            }, summaryOf(ctx, id))
          ),
          mobile && h('span', { key: 'chev', style: css({ fontSize: '13px', color: C.faint }) }, '›')
        );
      })
    ),
    h('div', {
      style: css({
        padding: '12px 16px', borderTop: `1px solid ${C.line}`, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', gap: '8px',
        paddingBottom: mobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : '12px'
      })
    },
      /*
       * The answer to "is the fix actually live on my phone?" -- a question
       * this app has had to answer the slow way, by looking for behaviour.
       */
      h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', color: C.faint, wordBreak: 'break-all' }) }, s.prefVersion(APP_VERSION)),
      h('span', {
        ...tap(actions.copyVersion, s.prefCopy),
        style: css({ fontSize: '11.5px', color: state.versionCopied ? C.accent : C.muted, cursor: 'pointer', whiteSpace: 'nowrap' })
      }, state.versionCopied ? s.prefCopied : s.prefCopy)
    )
  );
}

/**
 * Which plan the account is on, in two lines; the whole story (and buying)
 * is one tap away, on the Plan page. Absent before plans exist.
 */
function planCard({ state, t, actions, mobile }, current) {
  const s = t.shell;
  const plan = state.user?.plan;
  if (!plan?.launch) return null;
  const plus = plan.plan === 'plus' && plan.enforced;
  const date = value => (value ? s.dateFull(value) : '');

  let badge = null;
  let line;
  if (!plan.enforced) line = s.prefPlanSoonLine(date(plan.launch));
  else if (plan.source === 'demo') line = s.planDemo;
  else if (plan.source === 'gift') { badge = s.prefPlanGift; line = s.prefPlanGiftLine(date(plan.until)); }
  else if (plan.source === 'trial') { badge = s.prefPlanTrial; line = s.prefPlanTrialLine(date(plan.until)); }
  else if (plan.billing?.subscription && LIVE.has(plan.billing.subscription.status)) line = s.prefPlanSubLine;
  else if (plus) line = s.prefPlanGrantLine(date(plan.until));
  else line = s.prefPlanFreeLine;

  const on = !mobile && current === 'plan';
  return h('div', {
    key: 'plan-card',
    ...tap(() => actions.setSettingsSection('plan'), s.prefSections.plan[0]),
    style: css({
      margin: '0 12px 12px', padding: '12px 14px', borderRadius: '12px', cursor: 'pointer',
      background: plus ? C.accentDeep + '55' : C.card,
      border: `1px solid ${on ? C.accent : plus ? C.accentDeep : C.stroke}`,
      display: 'flex', flexDirection: 'column', gap: '4px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px' }) },
      h('span', { style: css({ fontSize: '14px', fontWeight: 700, color: plus ? C.accentHi : C.text }) }, plus ? 'Liquidity+' : 'Free'),
      badge && h('span', {
        key: 'badge',
        style: css({
          padding: '1px 6px', borderRadius: '5px', background: C.accentDeep, fontFamily: MONO,
          fontSize: '9.5px', fontWeight: 700, letterSpacing: '.6px', color: C.accentHi
        })
      }, badge),
      h('span', { style: css({ flex: 1 }) }),
      h('span', { style: css({ fontSize: '12px', color: C.faint }) }, '›')
    ),
    h('span', { style: css({ fontSize: '11.5px', lineHeight: 1.45, color: C.muted }) }, line)
  );
}

/** What a section holds, in one line: the phone's list of sections reads it. */
function summaryOf({ state, t, fmt }, id) {
  const s = t.shell;
  const st = state.settings;
  if (id === 'general') {
    const lang = LANGUAGES.find(l => l.code === st.language)?.name || st.language;
    return [lang, t.nav[st.startTab] || t.nav.overview, st.unitSystem === 'imperial' ? t.units.imperial : t.units.metric].join(' · ');
  }
  if (id === 'currencies') return st.baseCurrency + ' · ' + s.prefSumRates(Object.keys(state.data?.rates || {}).length);
  if (id === 'finance') {
    const cash = (state.data?.cash || []).length;
    return (st.debtStrategy === 'balance' ? s.strategyBalance : s.strategyRate) + ' · ' + (cash ? s.prefSumCash(cash) : s.prefSumNoCash);
  }
  if (id === 'reminders') {
    const push = state.push || {};
    return push.on ? s.prefOn + ' · ' + s.pushLead[push.prefs?.leadDays ?? 1] : s.prefOff;
  }
  if (id === 'ai') {
    const server = state.ai?.server;
    return server?.available ? s.prefAiOf(server.used || 0, server.limit) : s.aiSection;
  }
  return state.user?.isGuest ? s.guest : (state.user?.discordHandle || state.user?.email || '—');
}

// ------------------------------------------------------------ right column --

function sectionBody(ctx, current) {
  const { state, t, actions, mobile } = ctx;
  const s = t.shell;
  const [label, lead] = s.prefSections[current] || s.prefSections.general;
  const saved = state.settingsSaved;

  const content = {
    general: generalSection,
    currencies: currenciesSection,
    finance: financeSection,
    reminders: remindersSection,
    ai: aiSection,
    account: accountSection,
    plan: planPage
  }[current] || generalSection;

  return h('div', {
    key: 'body-' + current,
    style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 })
  },
    h('div', {
      style: css({
        flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '10px',
        padding: mobile ? '10px 14px' : '18px 24px', borderBottom: `1px solid ${C.line}`
      })
    },
      mobile && h('div', {
        key: 'back',
        ...tap(() => actions.setSettingsSection(null), s.prefBack),
        style: css(squareButton('44px'))
      }, '‹'),
      h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }) },
        h('span', { style: css({ fontSize: mobile ? '17px' : '18px', fontWeight: 700, letterSpacing: '-0.3px' }) }, label),
        h('span', { style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.4 }) }, lead)
      ),
      /* Only where a choice is stored as it is made; Account and Plan act through their buttons. */
      AUTOSAVES.has(current) && h('span', {
        key: 'saved',
        style: css({ fontSize: '11.5px', color: saved ? C.accent : C.faint, whiteSpace: 'nowrap' })
      }, saved ? s.prefSaved : s.prefAutoSave),
      !mobile && closeButton(ctx, '40px')
    ),
    h('div', {
      style: css({
        flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px',
        padding: mobile ? '14px 14px calc(32px + env(safe-area-inset-bottom, 0px))' : '20px 24px 28px'
      })
    }, ...[].concat(content(ctx)).filter(Boolean))
  );
}

const squareButton = size => ({
  width: size, height: size, flex: `0 0 ${size}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: '10px', border: `1px solid ${C.stroke}`, color: C.textDim, fontSize: '18px', cursor: 'pointer'
});

const closeButton = ({ t, actions }, size) => h('div', {
  key: 'close',
  ...tap(actions.closeSettings, t.shell.close),
  title: t.shell.close,
  style: css(squareButton(size))
}, '×');

// --------------------------------------------------------------- pieces -----

/** A section's bordered card. */
const card = (children, { danger = false, gap = '12px', padding = '16px 18px', key = null, border = null } = {}) => h('div', {
  key,
  style: css({
    padding, borderRadius: '14px', display: 'flex', flexDirection: 'column', gap,
    border: `1px solid ${border || (danger ? C.danger + '66' : C.stroke)}`,
    background: danger ? C.danger + '0F' : C.panel
  })
}, ...children.filter(Boolean));

const title = (text, sub) => h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
  h('span', { style: css({ fontSize: '14px', fontWeight: 600 }) }, text),
  sub && h('span', { style: css({ fontSize: '12px', color: C.muted, lineHeight: 1.45 }) }, sub)
);

const note = (text, color = C.faint) => h('div', { style: css({ fontSize: '11.5px', lineHeight: 1.5, color }) }, text);

/** Segmented choice: a well with the picked option lifted and outlined. */
function segmented(options, active, onPick, { grid = null, mono = false } = {}) {
  return h('div', {
    style: css({
      display: grid ? 'grid' : 'flex', gridTemplateColumns: grid, flexWrap: grid ? null : 'wrap', gap: '4px', padding: '3px',
      borderRadius: '10px', background: C.bg, border: `1px solid ${C.stroke}`
    })
  },
    ...options.map(option => {
      const on = option.value === active;
      return h('div', {
        key: String(option.value),
        ...tap(() => onPick(option.value), option.label),
        'aria-pressed': String(on),
        style: css({
          flex: grid ? null : '1 1 auto', minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 12px', borderRadius: '7px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center',
          background: on ? C.line : 'transparent', boxShadow: on ? `inset 0 0 0 1px ${C.accent}` : 'none',
          color: on ? C.text : C.muted, fontSize: '12.5px', fontWeight: mono ? 700 : 600, fontFamily: mono ? MONO : SANS
        })
      }, option.label);
    })
  );
}

function toggle(on, onToggle, label) {
  return h('div', {
    ...tap(onToggle, label),
    role: 'switch', 'aria-checked': String(Boolean(on)),
    style: css({
      width: '52px', height: '30px', flex: '0 0 52px', borderRadius: '15px', position: 'relative', cursor: 'pointer',
      background: on ? C.accent : C.strokeHi, transition: 'background .15s'
    })
  },
    h('span', {
      style: css({
        position: 'absolute', top: '3px', left: on ? '25px' : '3px', width: '24px', height: '24px', borderRadius: '50%',
        background: C.text, transition: 'left .15s'
      })
    })
  );
}

/** A labelled row inside a card: what it is on the left, the control on the right (below it when narrow). */
const settingRow = (label, sub, control, { last = false, controlFlex = '0 1 280px', key = null } = {}) => h('div', {
  key,
  style: css({
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px', padding: '16px 18px',
    borderBottom: last ? 'none' : `1px solid ${C.line}`
  })
},
  h('div', { style: css({ flex: '1 1 220px', minWidth: 0 }) }, title(label, sub)),
  h('div', { style: css({ flex: controlFlex, minWidth: 0 }) }, control)
);

const linkCard = (label, sub, onClick, key) => h('div', {
  key,
  ...tap(onClick, label),
  class: 'pt-button',
  style: css({
    minHeight: '64px', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${C.stroke}`, background: C.card,
    display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer'
  })
},
  h('div', { style: css({ flex: 1, minWidth: 0 }) }, title(label, sub)),
  h('span', { style: css({ color: C.accent }) }, '→')
);

const linkGrid = (...cards) => h('div', {
  style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: '10px' })
}, ...cards);

/** Leaves Preferences for a tab: the fixed costs and the CSV tools live beside the entries. */
const goTo = (actions, tab) => () => { actions.closeSettings(); actions.setTab(tab); };

// --------------------------------------------------------------- General ----

function generalSection({ state, t, actions }) {
  const s = t.shell;
  const st = state.settings;
  return card([
    settingRow(s.prefLanguage, s.prefLangSub, segmented(
      LANGUAGES.map(lang => ({ value: lang.code, label: `${lang.code} · ${lang.name}` })),
      st.language, value => actions.setSettings({ language: value })
    ), { key: 'lang' }),
    settingRow(s.optStartTab, s.prefStartSub, segmented(
      TABS.map(value => ({ value, label: t.nav[value] })),
      st.startTab, value => actions.setSettings({ startTab: value })
    ), { controlFlex: '1 1 100%', key: 'start' }),
    /*
     * Storage is metric either way — this only changes how the vehicle reads,
     * so switching back and forth cannot corrupt anything.
     */
    settingRow(s.optUnits, st.unitSystem === 'imperial' ? t.units.imperialNote : t.units.metricNote, segmented(
      UNIT_SYSTEMS.map(value => ({ value, label: value === 'metric' ? t.units.metric : t.units.imperial })),
      st.unitSystem, value => actions.setSettings({ unitSystem: value })
    ), { key: 'units' }),
    h('div', { key: 'hide', style: css({ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 18px' }) },
      h('div', { style: css({ flex: 1, minWidth: 0 }) }, title(s.optHideBalances, s.optHideBalancesNote)),
      toggle(st.hideBalances, () => actions.setSettings({ hideBalances: !st.hideBalances }), s.optHideBalances)
    )
  ], { gap: '0', padding: '0' });
}

// ---------------------------------------------------- Currencies & rates ----

/** Four decimals is more than any bank quotes; trailing zeros are noise. */
const rateText = rate => numberText(Number(Number(rate).toFixed(4)));

function currenciesSection(ctx) {
  const { state, t, actions } = ctx;
  const s = t.shell;
  const base = state.settings.baseCurrency;

  return [
    card([
      title(s.optBaseCurrency, s.prefBaseSub),
      segmented(
        /*
         * The suggested order, always. Putting the chosen currency first made
         * the whole row reshuffle on every pick -- the tiles moved under the
         * cursor and the only thing that should have changed was which one is
         * outlined. A currency in use that is not on the list is appended.
         */
        [...SUGGESTED_CURRENCIES.slice(0, 8), base]
          .filter((code, index, all) => all.indexOf(code) === index)
          .map(code => ({ value: code, label: code })),
        base, value => actions.setSettings({ baseCurrency: value }),
        { grid: 'repeat(auto-fill, minmax(64px, 1fr))', mono: true }
      )
    ], { key: 'base' }),
    ratesCard(ctx)
  ];
}

/**
 * Exchange rates and inflation, one row per currency.
 *
 * Rates are hand-entered and fetched on request -- a tracker that could only
 * use a feed would be unusable the moment the feed was down, or when somebody
 * wanted the rate their bank actually gave them. A rate typed here is stamped
 * with the month on screen, so the months already closed keep theirs.
 *
 * Inflation is used for nothing except valuing cash positions, and stays empty
 * until somebody fills it in: an assumed rate would put a number nobody
 * supplied into their accounts.
 */
function ratesCard({ state, t, fmt, inputs, actions, mobile }) {
  const s = t.shell;
  const base = state.settings.baseCurrency;
  const rates = state.data?.rates || {};
  const missing = state.metrics?.missingRates || [];
  const inflation = state.data?.inflation || [];
  const added = state.settingsAddedCodes || [];
  const codes = [...new Set([
    ...Object.keys(rates), ...missing, ...added,
    ...inflation.map(row => row.currency),
    ...(state.data?.holdings || []).map(row => row.currency),
    ...(state.data?.cash || []).map(row => row.currency)
  ].filter(code => code && code !== base))].sort();

  const saveRate = (code, current) => {
    const raw = inputs.value('rate_' + code).trim();
    if (!raw) return;
    const rate = inputs.number('rate_' + code);
    /* Zero, a negative rate or an unreadable one is said out loud, never dropped in silence. */
    if (!(rate > 0)) return actions.showError(s.rateNeeded);
    if (rate === current) return;
    actions.setRate(code, rate);
  };
  const saveInflation = (code, stored) => {
    const raw = inputs.value('infl-' + code).trim();
    if (!raw) { if (stored) actions.removeInflation(code); return; }
    const rate = inputs.number('infl-' + code);
    if (!Number.isFinite(rate)) return actions.showError(s.inflationNeeded);
    if (stored && stored.rate === rate) return;
    actions.setInflation(code, rate);
  };

  const columns = mobile ? '48px minmax(0,1fr) minmax(0,76px) 32px' : '70px minmax(0,1fr) minmax(0,140px) minmax(0,1.4fr) 36px';
  const field = (name, value, placeholder, unit, { onChange, disabled = false, alert = false, aria }) => h('div', {
    style: css({
      display: 'flex', alignItems: 'center', gap: '6px', height: '42px', minWidth: 0, padding: '0 12px',
      borderRadius: '10px', border: `1px solid ${alert ? C.danger : disabled ? C.line : C.stroke}`,
      background: disabled ? C.card : C.bg
    })
  },
    input(inputs, name, placeholder, {
      type: 'number', value, disabled, ariaLabel: aria, onChange, onEnter: onChange,
      padding: '0', style: { flex: 1, minWidth: 0, background: 'transparent', border: '0', fontFamily: MONO, fontSize: '14px', height: '40px' }
    }),
    /* Named in the card's own sentence; on a phone the field needs the room more. */
    !mobile && h('span', { key: 'unit', style: css({ fontFamily: MONO, fontSize: '11px', color: C.faint }) }, unit)
  );

  const row = code => {
    const isBase = code === base;
    const from = isBase ? null : actions.rateRow(code);
    const inForce = from ? from.rate : null;
    const unset = !isBase && !inForce;
    const stored = inflation.find(r => r.currency === code);
    /* Newest first, the standing rate (no month) last. */
    const history = isBase ? [] : [...actions.rateHistory(code)]
      .sort((x, y) => String(y.month || '').localeCompare(String(x.month || ''))).slice(0, 6);
    const sub = isBase ? s.prefBase : from && from.month ? s.rateFrom(fmt.monthName(from.month)) : unset ? s.prefNoRate : '';
    const chips = h('div', {
        key: 'chips',
        style: css({ gridColumn: mobile ? '1 / -1' : 'auto', display: 'flex', gap: '4px', overflowX: 'auto', minWidth: 0 })
      },
        ...history.map((entry, i) => h('div', {
          key: String(entry.month || 'standing'),
          ...tap(() => actions.removeRate(code, entry.month), s.rateDropMonth),
          title: s.rateDropMonth,
          style: css({
            flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '1px', padding: '4px 8px', borderRadius: '7px', cursor: 'pointer',
            border: `1px solid ${i === 0 ? C.strokeHi : C.line}`, background: i === 0 ? C.line : 'transparent'
          })
        },
          h('span', { style: css({ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.6px', color: C.muted }) },
            (entry.month ? fmt.monthName(entry.month) : s.rateStanding).toUpperCase()),
          h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: i === 0 ? C.text : C.textDim }) },
            fmt.number(Number(entry.rate)),
            /* Marked: FILL HISTORY leaves a hand-entered rate alone, and this says which those are. */
            entry.source === 'manual' && h('span', { style: css({ color: C.accent, marginLeft: '4px' }) }, s.rateManual))
        ))
      );
    /* On a phone the × keeps its column and the months take a line of their own below. */
    const remove = isBase ? h('span', { key: 'no-remove' }) : h('div', {
        key: 'remove',
        ...(isBase || (!inForce && !added.includes(code)) ? {} : tap(() => { actions.removeRate(code); actions.forgetAddedCurrency(code); }, s.prefRemoveCurrency)),
        title: isBase ? null : s.prefRemoveCurrency,
        style: css({
          width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', fontSize: '16px',
          color: isBase || (!inForce && !added.includes(code)) ? 'transparent' : C.faint,
          cursor: isBase ? 'default' : 'pointer'
        })
      }, '×');

    return h('div', {
      key: code,
      style: css({
        display: 'grid', gridTemplateColumns: columns, gap: '10px 12px', alignItems: 'center',
        padding: '10px 4px', borderBottom: `1px solid ${C.line}`
      })
    },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
        h('span', { style: css({ fontFamily: MONO, fontSize: '14px', fontWeight: 700, color: isBase ? C.text : unset ? C.danger : C.accent }) }, code),
        sub && h('span', { key: 'sub', style: css({ fontSize: '10.5px', color: C.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, sub)
      ),
      field('rate_' + code, isBase ? '1' : inForce ? rateText(inForce) : undefined, s.rate, base, {
        disabled: isBase, alert: unset, aria: s.a11yRate(code), onChange: isBase ? null : () => saveRate(code, inForce)
      }),
      field('infl-' + code, stored ? numberText(stored.rate) : undefined, '—', '%', {
        aria: s.inflationSection + ' ' + code, onChange: () => saveInflation(code, stored)
      }),
      ...(mobile ? [remove, history.length ? chips : null] : [chips, remove])
    );
  };

  const addable = SUGGESTED_CURRENCIES.slice(0, 8).filter(code => code !== base && !codes.includes(code));

  return card([
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '10px' }) },
      h('div', { style: css({ flex: '1 1 240px', minWidth: 0 }) },
        title(s.prefRatesTitle, s.prefRatesSub(base, fmt.monthName(state.month)))),
      h('div', { style: css({ display: 'flex', gap: '6px', flexWrap: 'wrap' }) },
        button(s.rateBackfill, actions.backfillRates, {
          tone: 'muted', style: { height: '38px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '9px', opacity: state.busy ? 0.5 : 1 }
        }),
        button(s.rateRefresh, actions.refreshRates, {
          style: { height: '38px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '9px', opacity: state.busy ? 0.5 : 1 }
        })
      )
    ),
    /*
     * FETCH stamps today's rate with today's month, so standing on March and
     * pressing it changes nothing in March. Said out loud, because the button
     * is otherwise silent about it.
     */
    state.month !== currentMonth() && note(s.rateRefreshNote(fmt.monthName(currentMonth()))),
    !mobile && h('div', {
      key: 'head',
      style: css({
        display: 'grid', gridTemplateColumns: columns, gap: '12px', padding: '0 4px',
        fontFamily: MONO, fontSize: '10px', letterSpacing: '1.1px', color: C.muted
      })
    }, h('span', null, s.prefColCurrency), h('span', null, s.prefColRate), h('span', null, s.prefColInflation), h('span', null, s.prefColHistory), h('span', null)),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.strokeHi}` }) },
      row(base), ...codes.map(row)),
    addable.length > 0 && h('div', { key: 'add', style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }) },
      h('span', { style: css({ fontSize: '12px', color: C.muted }) }, s.prefAddCurrency),
      ...addable.map(code => h('div', {
        key: code,
        ...tap(() => actions.addSettingsCurrency(code), '+ ' + code),
        class: 'pt-button',
        style: css({
          height: '34px', display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: '8px',
          border: `1px dashed ${C.strokeHi}`, fontFamily: MONO, fontSize: '12px', color: C.textDim, cursor: 'pointer'
        })
      }, '+ ' + code))
    ),
    note(s.rateBackfillNote),
    note(s.prefInflationNote)
  ], { key: 'rates' });
}

// ---------------------------------------------------------------- Finance ---

function financeSection(ctx) {
  const { state, t, actions } = ctx;
  const s = t.shell;
  const strategySub = { rate: s.prefStrategyRateSub, balance: s.prefStrategyBalanceSub };

  return [
    card([
      title(s.optStrategy, s.prefStrategySub),
      h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '8px' }) },
        ...DEBT_STRATEGIES.map(value => {
          const on = state.settings.debtStrategy === value;
          return h('div', {
            key: value,
            ...tap(() => actions.setSettings({ debtStrategy: value })),
            'aria-pressed': String(on),
            style: css({
              minHeight: '64px', padding: '10px 14px', borderRadius: '12px', cursor: 'pointer',
              border: `1px solid ${on ? C.accent : C.stroke}`, background: on ? C.line : C.card,
              display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '3px'
            })
          },
            h('span', { style: css({ fontSize: '13.5px', fontWeight: 700, color: on ? C.text : C.textDim }) },
              value === 'rate' ? s.strategyRate : s.strategyBalance),
            h('span', { style: css({ fontSize: '12px', color: on ? C.accent : C.muted }) }, strategySub[value])
          );
        })
      )
    ], { key: 'strategy' }),
    savingsCard(ctx),
    linkGrid(
      linkCard(s.prefFixedTitle, s.prefFixedSub, goTo(actions, 'cash'), 'fixed'),
      linkCard(s.prefCsvTitle, s.prefCsvSub, goTo(actions, 'cash'), 'csv')
    )
  ];
}

/**
 * Savings, one balance per currency, each editable where it stands: these are
 * a handful of numbers somebody adjusts together, and a dialog per currency
 * would be four clicks to change two figures.
 */
function savingsCard({ state, t, fmt, inputs, actions }) {
  const s = t.shell;
  const base = state.settings.baseCurrency;
  const balances = state.data?.cash || [];
  const inBase = balance => {
    if (balance.currency === base) return balance.amount;
    const rate = actions.rateRow(balance.currency)?.rate;
    return rate ? balance.amount * rate : null;
  };
  const converted = balances.map(inBase);
  const total = converted.every(value => value !== null) ? converted.reduce((sum, value) => sum + value, 0) : null;
  const offered = [...new Set([base, ...SUGGESTED_CURRENCIES.slice(0, 8), ...Object.keys(state.data?.rates || {})])]
    .filter(code => !balances.some(b => b.currency === code));

  const saveBalance = balance => {
    const amount = inputs.number('bal_' + balance.currency);
    if (amount === null || amount === balance.amount) return;
    actions.setCashBalance(balance.currency, amount);
  };
  const addBalance = () => {
    const code = (inputs.value('newBalanceCurrency') || '').trim().toUpperCase();
    const amount = inputs.number('newBalanceAmount');
    if (!/^[A-Z]{3}$/.test(code) || !amount) return actions.showError(s.balanceNeeded);
    actions.setCashBalance(code, amount);
    inputs.clear(['newBalanceAmount']);
  };

  return card([
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
      h('div', { style: css({ flex: '1 1 200px', minWidth: 0 }) }, title(s.prefCashTitle, s.prefCashSub)),
      balances.length > 0 && total !== null && h('span', {
        key: 'total', style: css({ fontFamily: MONO, fontSize: '13px', fontWeight: 700, color: C.accent })
      }, '≈ ' + fmt.money(total))
    ),
    ...balances.map((balance, i) => h('div', {
      key: balance.currency,
      style: css({ display: 'grid', gridTemplateColumns: '62px minmax(0,1fr) auto 36px', gap: '10px', alignItems: 'center', minHeight: '48px', borderBottom: `1px solid ${C.line}` })
    },
      h('span', { style: css({ fontFamily: MONO, fontSize: '13px', fontWeight: 700, color: C.accent }) }, balance.currency),
      input(inputs, 'bal_' + balance.currency, numberText(balance.amount), {
        type: 'number', value: numberText(balance.amount), ariaLabel: s.a11yBalance(balance.currency), radius: '10px',
        style: { height: '42px', minWidth: 0, fontFamily: MONO, fontSize: '14px', fontWeight: 700, background: C.bg },
        onChange: () => saveBalance(balance), onEnter: () => saveBalance(balance)
      }),
      h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) },
        balance.currency === base || converted[i] === null ? '' : '≈ ' + fmt.money(converted[i])),
      h('div', {
        ...tap(() => actions.setCashBalance(balance.currency, 0), s.removeLabel || '×'),
        class: 'pt-danger-hover',
        style: css({ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.faint, fontSize: '16px', cursor: 'pointer' })
      }, '×')
    )),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: 'minmax(0,110px) minmax(0,1fr) auto', gap: '8px' }) },
      select(inputs, 'newBalanceCurrency', offered.map(code => ({ value: code, label: code })),
        { height: '44px', borderRadius: '10px', fontFamily: MONO, fontSize: '13px', background: C.bg }, { value: offered[0] }),
      input(inputs, 'newBalanceAmount', s.amount, {
        type: 'number', ariaLabel: s.a11yBalanceAmount, radius: '10px',
        style: { height: '44px', minWidth: 0, fontFamily: MONO, fontSize: '14px', background: C.bg }, onEnter: addBalance
      }),
      button(s.prefAddBalance, addBalance, { style: { height: '44px', display: 'flex', alignItems: 'center', padding: '0 16px', borderRadius: '10px', whiteSpace: 'nowrap' } })
    )
  ], { key: 'savings' });
}

// ---------------------------------------------------------- Notifications ---

/**
 * Payment reminders: a notification on this phone or computer before a
 * fixed cost, a subscription or an instalment is due.
 *
 * Turned on per device -- each browser asks its own permission -- while how
 * early and what about belong to the account. A browser that cannot receive
 * them says why: an iPhone wants the app on its Home Screen first, the app
 * shells have no web push, an old browser has none at all.
 */
function remindersSection({ state, t, actions }) {
  const s = t.shell;
  const push = state.push || {};
  if (!push.loaded && !push.loading) actions.loadPush?.();

  const why = state.user?.isGuest ? s.pushGuest
    : push.support === 'ios-home' ? s.pushIosHome
      : push.support === 'native' ? s.pushNative
        : push.support === 'none' ? s.pushNone : null;
  if (why) return card([title(s.pushThisDevice, s.pushNote), note(why, C.textDim)], { key: 'why' });
  if (!push.loaded) return card([title(s.pushThisDevice, s.pushNote)], { key: 'loading' });

  const prefs = push.prefs || { leadDays: 1, bills: true, debts: true, income: false };
  const status = push.on ? s.pushOnNote(push.devices || 1) : push.permission === 'denied' ? s.pushOffBlocked : s.pushOffNote;
  const check = (key, label) => {
    const on = Boolean(prefs[key]);
    return h('div', {
      key, ...tap(() => actions.setPushPrefs({ [key]: !on }), label), role: 'checkbox', 'aria-checked': String(on),
      style: css({
        minHeight: '48px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 12px', borderRadius: '10px', cursor: 'pointer',
        border: `1px solid ${on ? C.accent : C.strokeHi}`, background: on ? C.accentDeep + '55' : 'transparent'
      })
    },
      h('span', {
        style: css({
          width: '20px', height: '20px', flex: '0 0 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px',
          border: `1px solid ${on ? C.accent : C.strokeHi}`, background: on ? C.accent : 'transparent', color: C.bg, fontSize: '13px', fontWeight: 700
        })
      }, on ? '✓' : ''),
      h('span', { style: css({ fontSize: '13px' }) }, label)
    );
  };

  return [
    h('div', {
      key: 'push',
      style: css({ borderRadius: '14px', border: `1px solid ${push.on ? C.accentDeep : C.stroke}`, background: C.panel, display: 'flex', flexDirection: 'column' })
    },
      h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 18px' }) },
        h('div', { style: css({ flex: 1, minWidth: 0 }) }, title(s.pushThisDevice, status)),
        toggle(push.on, () => (push.busy ? null : push.on ? actions.disablePush() : actions.enablePush()), s.pushThisDevice)
      ),
      push.permission === 'denied' && !push.on && h('div', { key: 'blocked', style: css({ padding: '0 18px 16px' }) }, note(s.pushBlocked, C.danger)),
      push.on && h('div', {
        key: 'options',
        style: css({ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 18px', borderTop: `1px solid ${C.line}` })
      },
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
          h('span', { style: css({ fontSize: '12px', color: C.muted }) }, s.prefPushWhen),
          segmented([0, 1, 3].map(value => ({ value, label: s.pushLead[value] })), prefs.leadDays,
            value => actions.setPushPrefs({ leadDays: value }), { grid: 'repeat(3, minmax(0, 1fr))' })
        ),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
          h('span', { style: css({ fontSize: '12px', color: C.muted }) }, s.pushWhat),
          h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '8px' }) },
            check('bills', s.pushBills), check('debts', s.pushDebts), check('income', s.pushIncome))
        ),
        h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }) },
          button(push.testing ? '…' : push.tested ? s.prefTestSent : s.pushTest, actions.testPush, {
            tone: 'muted',
            style: { height: '40px', display: 'flex', alignItems: 'center', padding: '0 14px', borderRadius: '9px', color: push.tested ? C.accent : C.text, opacity: push.testing ? 0.6 : 1 }
          }),
          h('span', { style: css({ flex: '1 1 240px', fontSize: '11.5px', lineHeight: 1.5, color: C.faint }) }, s.pushPrivacy)
        )
      )
    )
  ];
}

// ------------------------------------------------------------- AI & privacy --

/**
 * Category suggestions: what they do, what leaves the app, and how much of the
 * month's allowance is used. Nothing to switch: the button that asks for them
 * sits on the import review screen, where the decision to send a description
 * is actually made.
 */
function aiSection({ state, t, actions }) {
  const s = t.shell;
  const ai = state.ai;
  if (!ai) {
    // Loaded on demand; nothing here is load-bearing.
    actions.loadAiSettings();
    return null;
  }
  const server = ai.server || {};
  const used = server.used || 0;
  const share = server.limit ? Math.min(1, used / server.limit) : 0;
  const tile = (head, body, good) => h('div', {
    style: css({
      padding: '10px 12px', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '4px',
      background: good ? C.accentDeep + '44' : C.danger + '14', border: `1px solid ${good ? C.accentDeep : C.danger + '55'}`
    })
  },
    h('span', { style: css({ fontFamily: MONO, fontSize: '10px', fontWeight: 700, letterSpacing: '.8px', color: good ? C.accentHi : C.danger }) }, head),
    h('span', { style: css({ fontSize: '12.5px', color: C.textDim }) }, body)
  );

  return [
    card([
      title(s.prefAiTitle, s.prefAiSub),
      !server.offered ? note(s.prefAiOff, C.muted)
        : server.available ? h('div', { key: 'usage', style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
          h('div', { style: css({ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px' }) },
            h('span', { style: css({ color: C.textDim }) }, s.prefAiUsed),
            h('span', { style: css({ fontFamily: MONO, color: C.text }) }, s.prefAiOf(used, server.limit))),
          h('div', { style: css({ height: '6px', borderRadius: '3px', background: C.line, overflow: 'hidden' }) },
            h('div', { style: css({ width: Math.max(1, Math.round(share * 100)) + '%', height: '100%', background: C.accent }) }))
        ) : note(s.aiServerOff, C.muted),
      h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '8px' }) },
        tile(s.prefAiSend, s.prefAiSendBody, true), tile(s.prefAiNever, s.prefAiNeverBody, false)),
      note(s.prefAiNote)
    ], { key: 'ai' }),
    linkCard(s.prefReviewTitle, s.prefReviewSub, goTo(actions, 'overview'), 'review')
  ];
}

// --------------------------------------------------------- Account & data ---

function accountSection(ctx) {
  const { state, metrics, t, actions } = ctx;
  const s = t.shell;
  const user = state.user || {};
  const who = user.isGuest ? s.guest : (user.email || user.displayName || '—');
  const initial = (user.discordHandle || user.email || user.displayName || '?').replace(/^@/, '').charAt(0).toUpperCase();
  const entries = metrics?.totals?.entryCount ?? 0;

  const action = (label, sub, onClick, key, color = C.text) => h('div', {
    key,
    ...tap(onClick, label),
    class: 'pt-nav',
    style: css({ minHeight: '56px', display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 18px', borderBottom: `1px solid ${C.line}`, cursor: 'pointer' })
  },
    h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }) },
      h('span', { style: css({ fontSize: '13.5px', fontWeight: 600, color }) }, label),
      h('span', { style: css({ fontSize: '12px', color: C.muted }) }, sub)),
    h('span', { style: css({ fontSize: '13px', color: C.faint }) }, '›')
  );

  return [
    card([
      h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px' }) },
        h('div', {
          style: css({
            width: '48px', height: '48px', flex: '0 0 48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
            background: C.line, border: `1px solid ${C.strokeHi}`, fontSize: '19px', fontWeight: 700, color: C.accentHi
          })
        }, initial),
        h('div', { style: css({ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }) },
          h('span', { style: css({ fontSize: '14.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, who),
          user.hasDiscord && h('span', { key: 'discord', style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.accent }) }, 'Discord · ' + (user.discordHandle || ''))),
        h('span', { style: css({ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1px', color: C.textDim }) },
          h('span', { style: css({ width: '7px', height: '7px', borderRadius: '50%', background: state.online ? C.accent : C.danger }) }),
          state.online ? s.prefSyncedEntries(entries) : s.offline)
      )
    ], { key: 'who' }),
    h('div', {
      key: 'actions',
      style: css({ borderRadius: '14px', border: `1px solid ${C.stroke}`, background: C.panel, display: 'flex', flexDirection: 'column', overflow: 'hidden' })
    },
      user.isGuest && action(s.prefClaim, s.prefClaimSub, actions.openClaim, 'claim', C.accent),
      // Linking is only offered when the server actually has Discord configured.
      state.discordEnabled && !user.hasDiscord && !user.isGuest && action(s.prefLinkDiscord, s.prefLinkDiscordSub, () => actions.startDiscordSignIn({ link: true }), 'link'),
      state.discordEnabled && user.hasDiscord && user.hasPassword && action(s.prefUnlinkDiscord, s.prefUnlinkSub, actions.unlinkDiscord, 'unlink'),
      /*
       * Offered to any real account. Without it a Discord-only user could never
       * gain a password, and the unlink control above -- which waits for one --
       * could never appear.
       */
      !user.isGuest && !state.passwordOpen && action(
        user.hasPassword ? s.prefChangePw : s.prefAddPw,
        user.hasPassword ? s.prefChangePwSub : s.prefAddPwSub, actions.togglePassword, 'password'),
      !user.isGuest && state.passwordOpen && h('div', { key: 'password-form', style: css({ padding: '12px 14px', borderBottom: `1px solid ${C.line}` }) },
        passwordForm(ctx)),
      /* The owner's way into the admin panel; nobody else is offered it. */
      user.isAdmin && action(s.prefMenuAdmin, s.prefAdminSub, actions.openAdmin, 'admin', C.accentHi),
      action(s.prefMenuSignOut, s.prefSignOutSub, actions.signOut, 'signout', C.textDim)
    ),
    yourDataCard(ctx),
    h('div', { key: 'legal' }, legalLinks({ t, lang: state.lang, base: state.siteBase }))
  ];
}

/**
 * The account's own data: a copy of all of it, and the way to delete it.
 *
 * Both are rights the privacy policy promises, and both have to be reachable
 * without writing to anybody. Deleting asks twice -- once by opening, once by
 * confirming with the password -- because nothing brings the ledger back.
 */
function yourDataCard({ state, t, inputs, actions }) {
  const s = t.shell;
  const user = state.user || {};
  const needsPassword = !user.isGuest && user.hasPassword;

  const download = async () => {
    const text = await actions.exportAll();
    if (!text) return;
    saveFile('portfolio-' + localDay() + '.json', text, 'application/json');
  };
  const confirmDelete = () => {
    const password = needsPassword ? inputs.value('deletePassword') : undefined;
    if (needsPassword && !password) return actions.showError(s.deletePasswordNeeded);
    actions.deleteAccount(password);
  };
  const pill = (label, onClick, style) => button(label, onClick, {
    tone: 'muted', style: { height: '44px', display: 'flex', alignItems: 'center', padding: '0 16px', borderRadius: '10px', ...style }
  });

  return card([
    title(s.prefYourData, s.prefDataSub),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '8px' }) },
      pill(s.prefDownload, download, { background: C.bg, border: `1px solid ${C.strokeHi}` }),
      !state.deleteOpen && pill(user.isGuest ? s.deleteDemo : s.deleteAccount, () => actions.askDeleteAccount(true),
        { background: 'transparent', border: `1px solid ${C.danger}88`, color: C.danger })
    ),
    state.deleteOpen && h('div', { key: 'delete-confirm', style: css({ display: 'flex', flexDirection: 'column', gap: '9px' }) },
      h('div', { style: css({ fontSize: '12.5px', lineHeight: 1.5, color: C.text }) }, user.isGuest ? s.deleteDemoWarning : s.deleteWarning),
      needsPassword && input(inputs, 'deletePassword', s.password, {
        type: 'password', radius: '10px', style: { height: '44px', width: '100%', background: C.bg }, onEnter: confirmDelete
      }),
      h('div', { style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap' }) },
        pill(s.cancel, () => actions.askDeleteAccount(false)),
        pill(s.deleteForever, confirmDelete, { background: C.danger, color: C.text, opacity: state.busy ? 0.6 : 1 })
      )
    )
  ], { danger: true, key: 'data' });
}

// ------------------------------------------------------------------ Plan ----

function planPage(ctx) {
  const section = planSection(ctx);
  return section ? card([section], { key: 'plan' }) : null;
}

/**
 * Which plan the account is on, and what that means for it.
 *
 * Absent until a launch date exists: before then there is nothing to be on,
 * and a panel announcing "Free" would read as something taken away. Once the
 * date is announced it says when, and what this account will have from then.
 */
export function planSection({ state, t, actions, inputs }) {
  const s = t.shell;
  const plan = state.user?.plan;
  if (!plan?.launch) return null;

  const plus = plan.plan === 'plus';
  const limited = plan.enforced && !plus;
  const billing = plan.billing || {};
  const subscription = billing.subscription;
  const live = Boolean(subscription && LIVE.has(subscription.status));

  let status;
  if (!plan.enforced) {
    status = s.planStarts(s.dateFull(plan.launch))
      + (plan.giftUntil ? ' ' + s.planGiftAhead(s.dateFull(plan.giftUntil)) : '');
  } else if (plan.source === 'demo') {
    status = s.planDemo;
  } else if (live) {
    status = subscriptionLine(s, subscription, state.lang || state.settings?.language || 'EN');
  } else if (plan.source === 'trial') {
    status = s.planTrial(s.dateFull(plan.until));
  } else if (plan.source === 'gift') {
    status = s.planGift(s.dateFull(plan.until));
  } else if (plus) {
    status = plan.until ? s.planGrantedUntil(s.dateFull(plan.until)) : s.planGranted;
  } else {
    status = s.planFreeNote;
  }

  const note = text => h('div', { style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 }) }, text);
  const usage = text => h('div', {
    style: css({ fontFamily: MONO, fontSize: '11px', color: C.textDim, lineHeight: 1.6 })
  }, text);

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
    h('div', {
      style: css({ fontSize: '15px', fontWeight: 700, color: plus && plan.enforced ? C.accent : C.text })
    }, plan.enforced ? (plus ? 'Liquidity+' : 'Free') : 'Free · Liquidity+'),
    h('div', { style: css({ fontSize: '12px', color: C.textDim, lineHeight: 1.5 }) }, status),
    limited && h('div', { key: 'usage' },
      typeof plan.limits.csvImports === 'number' && usage(s.planUsedImports(plan.used?.csvImports || 0, plan.limits.csvImports)),
      usage(s.planUsedJournal(journalUsed(state), plan.limits.journalPerMonth)),
      /* Counted from what is on screen; a list not loaded yet is left out rather than guessed. */
      typeof plan.limits.backtestSessions === 'number' && state.backtests
        && usage(s.planUsedBacktests(state.backtests.length, plan.limits.backtestSessions)),
      typeof plan.limits.propAccounts === 'number' && state.prop
        && usage(s.planUsedProp(state.prop.length, plan.limits.propAccounts))
    ),
    !(plus && plan.enforced) && note(s.planIncludes),
    billingControls({ state, s, plan, billing, subscription, live, actions, note, inputs }),
    note(s.planKeepData)
  );
}

/** Subscription statuses in which it still stands. */
const LIVE = new Set(['active', 'trialing', 'past_due']);

/** One sentence on the subscription: what it costs and what happens next. */
function subscriptionLine(s, subscription, lang) {
  const date = subscription.periodEnd ? s.dateFull(subscription.periodEnd) : '—';
  if (subscription.status === 'past_due') return s.planSubPastDue;
  if (subscription.cancelAtEnd) return s.planSubCancelled(date);
  if (subscription.status === 'trialing') return s.planSubTrialing(date);
  const amount = subscription.amount === null || subscription.amount === undefined
    ? '—' : formatPrice(subscription.amount, subscription.currency || 'PLN', lang);
  return s.planSubActive(amount, subscription.interval, date);
}

/**
 * Buying, or managing what was bought.
 *
 * A subscription still standing is managed, never sold again. The 14-day
 * trial is offered the prices: somebody trying Liquidity+ out may decide
 * before it ends, and the first charge waits for the trial's last day either
 * way (the server carries it over into Stripe). The launch gift and a grant
 * are offered nothing -- a price beside "free until December" reads as a
 * catch. On Free, including after a subscription has lapsed, the prices are
 * offered.
 *
 * The prices are the shared ones. The app store shells only say where to buy:
 * a subscription sold inside them would be an in-app purchase by the stores'
 * own rules.
 */
function billingControls({ state, s, plan, billing, subscription, live, actions, note, inputs }) {
  const row = (...children) => h('div', {
    style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap' })
  }, ...children);
  const pill = (label, run) => button(label, run, {
    style: {
      height: '38px', padding: '0 14px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', flex: '1 1 0', fontSize: '10px', opacity: state.busy ? 0.5 : 1
    }
  });

  if (subscription && live) {
    return h('div', { key: 'billing-manage' },
      row(button(s.planManage, () => actions.openBillingPortal(), {
        tone: 'muted',
        style: { height: '38px', padding: '0 14px', display: 'flex', alignItems: 'center', opacity: state.busy ? 0.5 : 1 }
      }))
    );
  }
  const trial = plan.plan === 'plus' && plan.source === 'trial';
  /* Stripe will not start a trial under two days away; the server then charges at once. */
  const trialCarriesOver = until => Date.parse(until) - Date.now() > 49 * 60 * 60 * 1000;
  if (!billing.open || (plan.plan === 'plus' && !trial)) return null;
  if (state.native) return h('div', { key: 'billing-native' }, note(s.planNative));

  /*
   * The prices of the region this device is in, by its time zone, or the one
   * picked below. Any region may be picked; the card's country is checked
   * after the purchase, on the server.
   */
  const where = state.priceRegion || { region: 'eu', currency: 'PLN' };
  const prices = PRICES[where.region]?.[where.currency] || PRICES.eu.PLN;
  const lang = state.lang || state.settings?.language || 'EN';
  const money = amount => formatPrice(amount, PRICES[where.region]?.[where.currency] ? where.currency : 'PLN', lang).toUpperCase();
  const regionPicker = h('label', {
    key: 'region', style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '11px', color: C.faint })
  },
    s.planRegionLabel,
    select(inputs || createInputs(), 'priceRegion', PRICE_OPTIONS.map(({ region, currency }) => ({
      value: region + ':' + currency, label: s.planRegions[region + ':' + currency]
    })), { fontSize: '11px', padding: '5px 8px' }, {
      value: where.region + ':' + where.currency,
      onChange: value => { const [region, currency] = value.split(':'); actions.setPriceRegion(region, currency); }
    }));

  return h('div', { key: 'billing-buy', style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
    row(
      pill(s.planBuyMonth(money(prices.month)), () => actions.startCheckout('month')),
      pill(s.planBuyYear(money(prices.year)), () => actions.startCheckout('year'))
    ),
    regionPicker,
    trial && plan.until && h('div', { key: 'trial-note' }, note(trialCarriesOver(plan.until)
      ? s.planTrialBuyNote(s.dateFull(plan.until))
      : s.planTrialBuyNow)),
    note(s.planYearNote),
    note(s.planRenewNote)
  );
}


/**
 * Setting or changing the password.
 *
 * Unfolded rather than a dialog: it is three fields, and stacking a modal on a
 * modal for them would be ceremony. The current password is asked for only when
 * there is one -- an account created through Discord has none, and that is
 * exactly the account that most needs to be able to add one.
 */
function passwordForm({ state, t, inputs, actions }) {
  const s = t.shell;
  const user = state.user || {};

  const save = async () => {
    const next = inputs.value('pwNew');
    if (next.length < 8) return actions.showError(s.passwordShort);
    if (next !== inputs.value('pwAgain')) return actions.showError(s.passwordsDiffer);

    const done = await actions.setPassword({
      newPassword: next,
      ...(user.hasPassword ? { currentPassword: inputs.value('pwCurrent') } : {})
    });
    /* A refused current password is the only box to type again; the new one was fine. */
    inputs.clear(done ? ['pwCurrent', 'pwNew', 'pwAgain'] : ['pwCurrent']);
  };

  return h('div', {
    key: 'password-form',
    style: css({
      display: 'flex', flexDirection: 'column', gap: '8px',
      padding: '12px', borderRadius: '11px', border: '1px solid ' + C.stroke
    })
  },
    !user.hasPassword && h('div', {
      key: 'note',
      style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 })
    }, s.addPasswordNote),

    user.hasPassword && input(inputs, 'pwCurrent', s.currentPassword, {
      type: 'password', radius: '9px', style: { height: '38px', fontSize: '12.5px' }
    }),
    input(inputs, 'pwNew', s.newPassword, {
      type: 'password', radius: '9px', style: { height: '38px', fontSize: '12.5px' }
    }),
    input(inputs, 'pwAgain', s.passwordAgain, {
      type: 'password', radius: '9px',
      style: { height: '38px', fontSize: '12.5px' }, onEnter: save
    }),

    h('div', { style: css({ display: 'flex', gap: '8px' }) },
      button(s.save, save, { style: { flex: 1, opacity: state.busy ? 0.5 : 1 } }),
      button(s.cancel, actions.togglePassword, { tone: 'muted' })
    )
  );
}
